import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const AuthContext = createContext(null);

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const lastActivityRef = useRef(Date.now());

  // Ping interval to keep server awake
  useEffect(() => {
    const PING_INTERVAL = 45 * 1000; // 45 seconds
    const MAX_AWAKE_TIME = 60 * 60 * 1000; // 1 hour

    const intervalId = setInterval(() => {
      const now = Date.now();
      if (now - lastActivityRef.current <= MAX_AWAKE_TIME) {
        // Ping the server with a cache-buster to ensure it reaches the backend
        fetch(`${STRAPI_URL}/api/organ-systems?pagination[limit]=1&_t=${now}`).catch(() => {
          // Ignore errors, the goal is just to hit the server
        });
      }
    }, PING_INTERVAL);

    return () => clearInterval(intervalId);
  }, []);

  // Initialize auth state from localStorage on startup
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing stored user data:', e);
        // Clear corrupt state
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (identifier, password) => {
    try {
      const response = await fetch(`${STRAPI_URL}/api/auth/local`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Login failed. Please check your credentials.');
      }

      // Store in state
      setToken(data.jwt);
      setUser(data.user);

      // Store persistently
      localStorage.setItem('token', data.jwt);
      localStorage.setItem('user', JSON.stringify(data.user));

      return data.user;
    } catch (error) {
      console.error('AuthContext login error:', error);
      throw error;
    }
  };

  const logout = () => {
    // Clear state
    setToken(null);
    setUser(null);

    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  // Utility to make authenticated requests easily
  const authFetch = async (url, options = {}) => {
    lastActivityRef.current = Date.now(); // Update last activity timestamp

    const headers = {
      ...options.headers,
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      logout();
    }

    return response;
  };

  const value = {
    user,
    token,
    login,
    logout,
    authFetch,
    isAuthenticated: !!token,
    isLoading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
